import React, { useCallback, useMemo, useRef } from "react";
import RepeatableObjectField, {
  type FieldDef,
} from "@components/RepeatableField/RepeatableObjectField";
import {
  mapResourceTypesToOptions,
  type ResourceType,
} from "@features/vessel-call/vessel-call-status/MovementServices/form/utils.ts";
import type { MovementOutletContext } from "@features/vessel-call/vessel-call-status/MovementServices/MovementServicesContainer";
import { useMovementServiceFormStore } from "@features/vessel-call/vessel-call-status/MovementServices/store/useMovementServiceForm.store.ts";
import { useFormattedReference } from "@hooks/useFormattedReference.ts";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi.ts";
import { t } from "i18next";
import { FormProvider } from "react-hook-form";
import { useOutletContext } from "react-router-dom";

const LANG_BASE = "vessel-call.movement-services";

type AdditionalResourceRow = {
  additionalResourceType?: string;
  numberOfResources?: string;
};

const AdditionalResources: React.FC = () => {
  const { formData } = useOutletContext<MovementOutletContext>();
  const { setField } = useMovementServiceFormStore();

  const { options: resourceTypes } = useFormattedReference<ResourceType>(
    REFERENCE_DATA_TYPES.RESOURCE_TYPES,
    "bff-vessel-call",
  );

  const resourceOptions = useMemo(() => mapResourceTypesToOptions(resourceTypes), [resourceTypes]);

  const previousResourcesRef = useRef<AdditionalResourceRow[]>([]);

  const applyDefaultResourceCount = useCallback(
    (resources: AdditionalResourceRow[], previousResources: AdditionalResourceRow[]) => {
      let didPopulate = false;
      const next = resources.map((resource, index) => {
        const previousResource = previousResources[index];
        const previousResourceType = previousResource?.additionalResourceType?.trim() ?? "";
        const resourceType = resource.additionalResourceType?.trim() ?? "";
        const hadResourceType = previousResourceType.length > 0;
        const hasResourceType = resourceType.length > 0;
        const resourceCount = resource.numberOfResources;
        const hasResourceCount =
          resourceCount !== null &&
          typeof resourceCount !== "undefined" &&
          String(resourceCount).trim() !== "";

        if (hadResourceType && !hasResourceType && hasResourceCount) {
          didPopulate = true;
          return { ...resource, numberOfResources: "" };
        }

        if (!hadResourceType && hasResourceType && !hasResourceCount) {
          didPopulate = true;
          return { ...resource, numberOfResources: "1" };
        }

        return resource;
      });

      return { next, didPopulate };
    },
    [],
  );

  const fields: FieldDef[] = [
    {
      key: "additionalResourceType",
      label: t(`${LANG_BASE}.fields.additional-resource-type`),
      type: "autocomplete",
      options: resourceOptions,
    },
    {
      key: "numberOfResources",
      label: t(`${LANG_BASE}.fields.number-of-resources`),
      type: "number",
      maxChars: 4,
    },
  ];

  return (
    <FormProvider {...formData}>
      <RepeatableObjectField
        name="additionalResources"
        sectionLabel={t(`${LANG_BASE}.sections.additional-resources`)}
        label={t(`${LANG_BASE}.fields.additional-resource`)}
        addButtonLabel={t(`${LANG_BASE}.buttons.add-resource`)}
        fields={fields}
        minItems={1}
        onFieldChange={(_, arr: unknown[]) => {
          const { next, didPopulate } = applyDefaultResourceCount(
            arr as AdditionalResourceRow[],
            previousResourcesRef.current,
          );
          previousResourcesRef.current = next;
          if (didPopulate) {
            formData.setValue("additionalResources", next, {
              shouldValidate: true,
              shouldDirty: true,
            });
          }
          setField(
            "additionalResources",
            next as { additionalResourceType: string; numberOfResources: string }[],
          );
        }}
        testId="movement-additional-resources"
      />
    </FormProvider>
  );
};

export default AdditionalResources;
